import React, { useMemo, useRef, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import {
  Search,
  Inbox,
  Bot,
  Building2,
  Users,
  X,
  Globe,
  AlertTriangle,
  Bell,
  Download,
  Loader2,
  MessageCircle,
  MessageSquare,
  MessageSquarePlus,
  Camera,
  Folder,
  Tag,
  ChevronDown,
  UserRound,
  Pin,
} from "lucide-react";
import { Link } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import CommerceSelector from "./CommerceSelector";
import ContactAvatar from "./ContactAvatar";
import { Skeleton } from "../ui/Skeleton";
import { EmptyState } from "../ui/EmptyState";
import { cn } from "../../utils/cn";
import { toPlainText } from "../../utils/whatsappFormat";
import { FOLDER_COLOR_STYLES, DEFAULT_FOLDER_COLOR } from "./folderColors";
import { ticketLabel } from "./tickets";
import useHelpdeskLabels from "./useHelpdeskLabels";
import LabelChip from "./LabelChip";
import ConversationRowMenu from "./ConversationRowMenu";
import { isPinnable } from "../../services/helpdeskPinsService";

dayjs.extend(relativeTime);
dayjs.locale("es");

// Mini-badge de canal sobre el avatar (esquina inferior-derecha), estilo mockup:
// círculo sólido por canal + ícono lucide en blanco. Es EL indicador de canal de
// la fila (reemplaza al antiguo badge con SVG de marca y al sufijo "· WA").
// GOTCHA: --status-reverse existe como token CSS (púrpura, con variante dark)
// pero no está mapeado en tailwind.config, por eso va como arbitrary value.
// Canales cuyo texto trae marcado de WhatsApp (*negrita*, _cursiva_…).
// Misma lista que ConversationDetail: si se toca uno hay que tocar el otro.
const WHATSAPP_CHANNELS = ["whatsapp", "whatsapp_unofficial"];

const CHANNEL_BADGE = {
  whatsapp: { icon: MessageCircle, bg: "bg-status-success" },
  whatsapp_unofficial: { icon: MessageCircle, bg: "bg-status-success" },
  messenger: { icon: MessageSquare, bg: "bg-status-info" },
  instagram: { icon: Camera, bg: "bg-[rgb(var(--status-reverse))]" },
  portal: { icon: Globe, bg: "bg-status-neutral" },
  ai: { icon: Bot, bg: "bg-brand-500" },
  // Canal interno (asistente de gerencia): mismo gris neutro que portal — es el
  // tono más discreto de la paleta y este canal no compite por atención.
  internal: { icon: Building2, bg: "bg-status-neutral" },
};

// Exportado: el Excel de la bandeja (pages/helpdesk) reutiliza estas etiquetas
// para la columna Canal — una sola fuente de nombres legibles por canal.
export const CHANNEL_NAME = {
  whatsapp: "WhatsApp",
  whatsapp_unofficial: "WhatsApp",
  messenger: "Messenger",
  instagram: "Instagram",
  portal: "Portal",
  ai: "IA",
  internal: "Interno",
};

// Nombre visible del contacto de una conversación.
//
// El canal `internal` (asistente de gerencia) NO tiene contacto: ni
// contact_name ni external_id, y el user_id es el ObjectId de quien escribió.
// Se devuelve el nombre del canal para que la fila diga algo legible en vez de
// un id crudo. El resto de canales mantiene el orden de siempre y cae al id
// como último recurso (mejor un id que una fila en blanco).
//
// `t` es opcional para que el nombre del canal se muestre en el idioma activo
// (una constante de módulo no puede llamar a useTranslation). El nombre de un
// contacto real NUNCA pasa por t(): es dato, no texto de UI.
//
// Exportado: el Excel de la bandeja (pages/helpdesk) usa el mismo criterio para
// su columna de contacto.
export function conversationDisplayName(conv, getDisplayName, t = (key) => key) {
  if (conv?.channel === "internal") return t("Equipo interno");
  return (
    (getDisplayName && getDisplayName(conv?.user_id)) ||
    conv?.contact_name ||
    conv?.external_id ||
    conv?.user_id ||
    "—"
  );
}

const INBOX_TITLE = {
  "inbox.all": "Todas",
  "inbox.unassigned": "Sin asignar",
  "inbox.mine": "Mis casos",
  "inbox.sla": "Vencidos (SLA)",
  "inbox.ai": "Atendidas por IA",
  "inbox.ai_live": "IA en curso",
  "inbox.resolved": "Resueltos",
  "channel.whatsapp": "WhatsApp",
  "channel.messenger": "Messenger",
  "channel.instagram": "Instagram",
  "channel.portal": "Portal",
  "channel.groups": "Grupos",
  "channel.internal": "Interno",
};

function getInitial(name) {
  if (!name) return "?";
  const trimmed = String(name).trim();
  return trimmed[0]?.toUpperCase() || "?";
}

// "María Pérez Soto" → "María P." — nombre compacto para la pill de asignación.
// Si el nombre aún no resolvió (llega el ObjectId crudo), devuelve tal cual y
// el truncate de la pill lo contiene.
function shortAgentName(fullName) {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[1][0].toUpperCase()}.`;
}

// UNA inicial: el avatar de la pill mide 18px y el texto del panel no baja de
// 11px (por debajo deja de leerse). Dos letras a 11px se salen del círculo, y
// como el nombre corto del agente va inmediatamente al lado, la segunda inicial
// no aporta desambiguación alguna.
function agentInitials(fullName) {
  const first = String(fullName || "").trim().split(/\s+/).filter(Boolean)[0];
  return (first?.[0] || "?").toUpperCase();
}

function avatarTone(seed) {
  // No brand/warning tones (second visual pass): orange and brown avatars
  // read as accent/notes and, in dark, as the same brown as the agent bubble.
  const tones = [
    "bg-bg-subtle text-fg-muted",
    "bg-status-info-soft text-status-info",
    "bg-status-success-soft text-status-success",
    "bg-status-cc-soft text-status-cc",
  ];
  if (!seed) return tones[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return tones[h % tones.length];
}

// Devuelve la etiqueta de grupo temporal según la fecha del mensaje.
// La agrupación se aplana cuando el usuario está buscando (no aporta entonces).
function bucketForDate(date) {
  if (!date) return "Sin fecha";
  const d = dayjs(date);
  const now = dayjs();
  if (d.isSame(now, "day")) return "Hoy";
  if (d.isSame(now.subtract(1, "day"), "day")) return "Ayer";
  if (d.isAfter(now.subtract(7, "day"))) return "Esta semana";
  if (d.isSame(now, "year")) return d.format("MMMM");
  return d.format("MMM YYYY");
}

// Etiquetas visibles por fila: más de dos ya no se leen en la columna de la
// bandeja; el resto se resume en "+N" (con los nombres en el tooltip).
const MAX_ROW_LABELS = 2;

// Mantener presionado para abrir el menú de la fila en pantallas táctiles.
// 500 ms es el umbral habitual (WhatsApp Web/Android ~500 ms): más corto
// dispara el menú al scrollear con el dedo apoyado.
const LONG_PRESS_MS = 500;
// Temblor tolerado del dedo durante el long press: con 1–2 px de movimiento
// el gesto no puede cancelarse (en varios teléfonos costaría abrir el menú).
const LONG_PRESS_SLOP_PX = 10;

/**
 * Etiquetas de una conversación resueltas contra el catálogo. Los ids que ya no
 * existen (etiqueta borrada, catálogo aún cargando) no se pintan.
 * @param {object} conv
 * @param {Record<string, object>} labelsById
 * @returns {{visible: Array<object>, hidden: Array<object>}}
 */
export function rowLabels(conv, labelsById) {
  const known = (Array.isArray(conv?.labels) ? conv.labels : [])
    .map((id) => labelsById?.[id])
    .filter(Boolean);
  return { visible: known.slice(0, MAX_ROW_LABELS), hidden: known.slice(MAX_ROW_LABELS) };
}

function ConvSkeleton() {
  return (
    <div className="flex gap-3 px-4 py-3 border-b border-border">
      <Skeleton className="w-10 h-10 rounded-full shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Skeleton className="h-3.5 w-32" />
          <Skeleton className="h-3 w-12" />
        </div>
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  );
}

export default function ConversationList({
  conversations,
  total,
  loading,
  error,
  onRetry,
  inboxKey,
  // Título de la cabecera cuando la vista no es una bandeja fija (p. ej. la
  // de un número retirado, que se nombra por su teléfono).
  titleOverride,
  searchQuery,
  onSearchChange,
  commerceFilter,
  onCommerceFilterChange,
  // Filtro por etiqueta (id o null). Sin `onLabelFilterChange` el selector no
  // se pinta; el padre es quien lo manda como `label_id` en la consulta.
  labelFilter,
  onLabelFilterChange,
  selectedId,
  onSelect,
  newIds,
  getDisplayName,
  showAllNumbers,
  onToggleAllNumbers,
  // Índice de carpetas (memo estable del padre) y carpeta activa del rail:
  // el chip de carpeta de cada fila se suprime cuando la vista YA es esa
  // carpeta (repetirlo en todas las filas no distingue nada).
  foldersById,
  activeFolderId,
  // Exportar a Excel lo que la lista está mostrando (el handler vive en el
  // padre, que es quien tiene la lista visible y el detalle abierto).
  // exportProgress ({current,total}|null): el export pide el detalle de cada
  // conversación en secuencia y puede tardar; el botón muestra el avance.
  onExport,
  exportDisabled,
  exportProgress,
  // Abre "Escribir a repartidor". Solo se pasa a los roles autorizados (ver
  // useWriteToDriver): sin la prop el botón no se pinta.
  onWriteToDriver,
  // La búsqueda en los mensajes (sección "En los mensajes", en el padre)
  // tampoco encontró nada. Cuando eso pasa Y la lista está vacía, el padre
  // deja de renderizar esa sección y este vacío pasa a hablar por los dos:
  // dos estados vacíos encadenados decían lo mismo dos veces.
  messageSearchAlsoEmpty = false,
  // La fuente de atenciones por IA (getAILogs) no respondió: la lista está
  // vacía por degradación, no porque no haya atenciones. El vacío tiene que
  // decir ESO en vez de "No hay conversaciones".
  aiLogsError = false,
  // Fijadas del usuario de la sesión (Map id → pinnedAt ms, siempre definido
  // por el hook). Sin `pins` no hay grupo "Fijados" ni indicador: el backend
  // puede no exponer todavía el endpoint.
  pins,
  // (conv) => void: alterna el fijado. Sin ella no se pinta NINGUNA acción de
  // fijar (ni menú de fila ni clic derecho ni mantener presionado).
  onTogglePin,
}) {
  const { t } = useTranslation();
  const searchInputRef = useRef(null);

  const isMobile = useIsMobile();
  // Catálogo de etiquetas (caché de sesión): chips de cada fila y filtro.
  const { labels, byId: labelsById } = useHelpdeskLabels();
  const hasHeaderActions = !!onExport || !!onToggleAllNumbers || !!onWriteToDriver;

  const isFolderView = !!activeFolderId;
  // Bandeja por canal: el vacío tiene una causa concreta (canal sin tráfico o
  // sin vincular) y una salida concreta (la vista de canales).
  const isChannelView = String(inboxKey || "").startsWith("channel.");
  const sectionTitle = titleOverride
    ? titleOverride
    : isFolderView
    ? foldersById?.[activeFolderId]?.name || t("Carpeta")
    : t(INBOX_TITLE[inboxKey] ?? "Bandeja");
  const isSearching = !!searchQuery.trim();
  const canPin = !!onTogglePin && !!pins;

  // ── Menú de fila (fijar / desfijar) ─────────────────────────────────────
  // El estado vive ACÁ y no en cada fila: solo puede haber un menú abierto, y
  // un menú por fila multiplicaría 50 estados y 50 listeners de document.
  const listRef = useRef(null);
  const rowRefs = useRef(new Map()); // id → elemento de la fila (ancla de foco)
  const triggerRefs = useRef(new Map()); // id → botón disparador del menú
  // firedId: fila cuyo long press abrió el menú (el click que le sigue se
  // descarta solo en ESA fila; un tap en otra fila nunca se pierde).
  const longPress = useRef({ timer: null, firedId: null, x: 0, y: 0 });
  // { id, placement: "below" | "above", opener: "trigger" | "row" } | null
  const [menu, setMenu] = useState(null);
  const setRowRef = useCallback((id, el) => {
    if (el) rowRefs.current.set(id, el);
    else rowRefs.current.delete(id);
  }, []);
  const setTriggerRef = useCallback((id, el) => {
    if (el) triggerRefs.current.set(id, el);
    else triggerRefs.current.delete(id);
  }, []);
  const clearLongPress = useCallback(() => {
    if (longPress.current.timer) {
      clearTimeout(longPress.current.timer);
      longPress.current.timer = null;
    }
  }, []);
  const openMenu = useCallback((id, opener = "row") => {
    // Cerca del borde inferior de la lista el menú abre hacia arriba: abajo
    // quedaría recortado por el overflow-y-auto del contenedor.
    const row = rowRefs.current.get(id);
    const list = listRef.current;
    let placement = "below";
    if (row && list) {
      const r = row.getBoundingClientRect();
      const l = list.getBoundingClientRect();
      // 56px ≈ alto del menú de una acción + aire.
      if (l.height > 0 && r.bottom + 56 > l.bottom) placement = "above";
    }
    setMenu({ id, placement, opener });
  }, []);
  // closeMenu corre desde listeners de document (closure estable): lee el id
  // vigente de un ref para no re-suscribir los listeners en cada apertura.
  const menuRef = useRef(null);
  useEffect(() => {
    menuRef.current = menu;
  }, [menu]);
  const closeMenu = useCallback((restoreFocus = false) => {
    const current = menuRef.current;
    setMenu(null);
    if (restoreFocus && current) {
      // Después del render: fijar/desfijar mueve la fila entre "Fijados" y su
      // día (otro padre), React la vuelve a montar y el nodo de antes ya no
      // existe. Se busca el nuevo por id; el foco vuelve a quien abrió el menú.
      requestAnimationFrame(() => {
        const trigger = triggerRefs.current.get(current.id);
        const row = rowRefs.current.get(current.id);
        const target = current.opener === "trigger" && trigger ? trigger : row;
        target?.focus?.({ preventScroll: true });
      });
    }
  }, []);
  // La fila dueña del menú salió de la bandeja (refetch, filtro, cambio de
  // vista): el menú quedaría flotando sobre otra fila. Se cierra sin robar foco.
  useEffect(() => {
    if (!menu) return;
    if (!conversations.some((c) => c.id === menu.id)) setMenu(null);
  }, [conversations, menu]);
  // El timer del mantener-presionado no puede sobrevivir al desmontaje.
  useEffect(() => () => clearLongPress(), [clearLongPress]);

  // Agrupar conversaciones por bucket temporal. Al buscar, no agrupamos:
  // entregamos una lista plana de resultados (los grupos confunden cuando los
  // ítems vienen de fechas dispersas).
  const grouped = useMemo(() => {
    if (isSearching) return [{ label: null, items: conversations }];
    // Las fijadas van en su propio grupo arriba de todo: una fijada de octubre
    // no puede caer bajo "Octubre" mientras el resto se ordena por fecha.
    const pinnedItems = pins ? conversations.filter((c) => pins.has(c.id)) : [];
    const rest = pins ? conversations.filter((c) => !pins.has(c.id)) : conversations;
    const groups = new Map();
    for (const conv of rest) {
      const label = bucketForDate(conv.last_message_at || conv.created_at);
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(conv);
    }
    // Orden esperado: Hoy, Ayer, Esta semana, luego meses por orden de inserción
    // (que ya viene desc por last_message_at en el fetch).
    const order = ["Hoy", "Ayer", "Esta semana"];
    const sortedKeys = [
      ...order.filter((k) => groups.has(k)),
      ...[...groups.keys()].filter((k) => !order.includes(k)),
    ];
    const dated = sortedKeys.map((k) => ({ label: k, items: groups.get(k) }));
    return pinnedItems.length > 0
      ? [{ label: "Fijados", pinned: true, items: pinnedItems }, ...dated]
      : dated;
  }, [conversations, isSearching, pins]);

  return (
    <>
      {/* Header: título + contador a la izquierda y las dos acciones de baja
          frecuencia (Excel, números de notificaciones) como botones de ícono
          alineados a la derecha, con el mismo vocabulario que las acciones del
          header del chat (32px desde md, 44px táctiles). Antes eran un botón
          con texto y un enlace sueltos bajo el filtro: dos filas más de cromo
          que leían como restos de la cabecera.

          D3 · `gap-2 px-4 py-3` son utilidades de BOOTSTRAP con `!important`
          (App.js:9 lo carga después de Tailwind): una variante `md:` sobre
          ellas nunca gana. El ritmo de teléfono vive en helpdeskMobile.scss
          (`.hd-inbox-header`). */}
      <div className="hd-inbox-header flex flex-col gap-[8px] px-[16px] pt-[12px] pb-[12px] border-b border-border/60 bg-bg shrink-0">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex-1 min-w-0">
            {/* A6 · La barra de la suite (HelpdeskSuiteLayout) ya rotula la
                bandeja activa 44px más arriba: en móvil el título queda solo
                para lectores de pantalla. */}
            <h2
              className="max-md:sr-only text-[14px] leading-[20px] font-semibold text-fg truncate"
              title={sectionTitle}
            >
              {sectionTitle}
            </h2>
            {/* Contador de la vista. Mientras el export recorre el detalle de
                cada conversación, esta línea muestra el avance en su lugar: es
                el único texto de estado del header y así no aparece una fila
                extra durante la descarga. */}
            {/* Sin aria-live: `total` cambia con cada tecla del buscador y el
                export lo reescribe N veces; el botón ya expone aria-busy. */}
            <p className="text-[12px] leading-[16px] text-fg-muted tabular-nums truncate">
              {exportProgress
                ? t("Descargando {{current}}/{{total}}…")
                    .replace("{{current}}", exportProgress.current)
                    .replace("{{total}}", exportProgress.total)
                : total === 1
                  ? t("1 conversación")
                  : t("{{count}} conversaciones").replace("{{count}}", total)}
            </p>
          </div>

          {hasHeaderActions && (
            <div className="flex items-center gap-0.5 shrink-0 -my-2.5 md:my-0 -mr-2 md:-mr-1.5">
              {onWriteToDriver && (
                <button
                  type="button"
                  onClick={onWriteToDriver}
                  aria-label={t("Escribir a repartidor")}
                  title={t("Escribir a repartidor")}
                  className={cn(
                    "inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md text-fg-muted transition-colors",
                    "hover:bg-bg-muted hover:text-fg",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                  )}
                >
                  <MessageSquarePlus className="w-4 h-4" aria-hidden="true" />
                </button>
              )}

              {/* Descargar la bandeja VISIBLE (filtros incluidos) como .xlsx. El
                  title va en un span envolvente: un botón disabled no emite
                  eventos de mouse y el tooltip jamás aparecería sobre él. */}
              {onExport && (
                <span
                  title={
                    exportDisabled
                      ? t("No hay conversaciones para exportar")
                      : t("Descargar Excel")
                  }
                >
                  <button
                    type="button"
                    onClick={onExport}
                    disabled={exportDisabled || !!exportProgress}
                    aria-busy={!!exportProgress}
                    aria-label={t("Descargar Excel")}
                    className={cn(
                      "inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md text-fg-muted transition-colors",
                      "hover:bg-bg-muted hover:text-fg",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                      "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-fg-muted",
                    )}
                  >
                    {exportProgress ? (
                      <Loader2
                        className="w-4 h-4 animate-spin motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                    ) : (
                      <Download className="w-4 h-4" aria-hidden="true" />
                    )}
                  </button>
                </span>
              )}

              {/* Los números dedicados SOLO a notificaciones se ocultan del
                  inbox por defecto (su tráfico se captura igual, no se pierde).
                  Toggle de ícono: encendido va teñido de marca, igual que el
                  botón de búsqueda del chat cuando está abierto. */}
              {onToggleAllNumbers && (
                <button
                  type="button"
                  onClick={onToggleAllNumbers}
                  aria-pressed={!!showAllNumbers}
                  aria-label={
                    showAllNumbers
                      ? t("Ocultar números de notificaciones")
                      : t("Incluir números de notificaciones")
                  }
                  title={`${
                    showAllNumbers
                      ? t("Ocultar números de notificaciones")
                      : t("Incluir números de notificaciones")
                  } · ${t("Los números dedicados a notificaciones se ocultan del inbox por defecto")}`}
                  className={cn(
                    "inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md transition-colors",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                    showAllNumbers
                      ? "bg-brand-50 text-brand-700"
                      : "text-fg-muted hover:bg-bg-muted hover:text-fg",
                  )}
                >
                  <Bell className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Buscador: fila propia, superficie hundida sin borde (el borde
            aparece al enfocar). */}
        <div className="relative min-w-0">
          <Search
            className="absolute left-[10px] top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
            aria-hidden="true"
          />
          <input
            ref={searchInputRef}
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={isMobile ? t("Buscar…") : t("Buscar nombre, teléfono, mensaje…")}
            aria-label={t("Buscar conversación")}
            /* type="search" trae su propia aspa nativa en WebKit/Blink y se
               duplicaba con el botón de limpiar de abajo. Se oculta la nativa
               y se conserva la accesible, que además devuelve el foco. 16px en
               teléfono: por debajo iOS hace zoom al enfocar. */
            className="w-full h-[40px] md:h-[32px] pl-[34px] pr-[30px] rounded-lg border border-transparent bg-bg-subtle text-[16px] md:text-[13px] text-fg placeholder:text-fg-muted transition-colors focus:outline-none focus:bg-bg focus:border-border focus:ring-2 focus:ring-brand-500 [&::-webkit-search-cancel-button]:appearance-none"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => {
                onSearchChange("");
                searchInputRef.current?.focus();
              }}
              aria-label={t("Limpiar búsqueda")}
              className="absolute right-[6px] top-1/2 -translate-y-1/2 inline-flex w-[22px] h-[22px] items-center justify-center rounded text-fg-muted hover:bg-bg-muted hover:text-fg"
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </button>
          )}
        </div>

        {/* Filtros: comercio y etiqueta en UNA fila de dos mitades. Sin
            micro-rótulos aparte: sin filtro, cada control muestra el nombre
            de su dimensión ("Comercio", "Etiqueta") junto a su ícono. */}
        {(onCommerceFilterChange ||
          (onLabelFilterChange && (labels.length > 0 || labelFilter))) && (
          <div className="flex items-center gap-[8px] min-w-0">
            {onCommerceFilterChange && (
              <div className="flex-1 min-w-0">
                <CommerceSelector
                  value={commerceFilter || null}
                  onChange={(c) => onCommerceFilterChange(c || null)}
                  placeholder={t("Comercio")}
                  size="sm"
                />
              </div>
            )}

            {/* Etiqueta: selector simple (una a la vez), solo si el courier ya
                creó alguna. Con un filtro activo se pinta aunque el catálogo se
                haya vaciado, para que siempre se pueda quitar. */}
            {onLabelFilterChange && (labels.length > 0 || labelFilter) && (
              <div className="relative flex-1 min-w-0">
                <Tag
                  className="absolute left-[10px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted pointer-events-none"
                  aria-hidden="true"
                />
                <select
                  value={labelFilter || ""}
                  onChange={(e) => onLabelFilterChange(e.target.value || null)}
                  aria-label={t("Filtrar por etiqueta")}
                  className={cn(
                    "w-full h-[40px] md:h-[32px] pl-[30px] pr-[28px] py-0 appearance-none truncate rounded-md border bg-bg text-[13px]",
                    "focus:outline-none focus:ring-2 focus:ring-brand-500",
                    // Sin filtro se lee como el placeholder del comercio (muted);
                    // con filtro, valor a contraste completo y borde de acento.
                    labelFilter ? "border-brand-500 text-fg" : "border-border text-fg-muted",
                  )}
                >
                  <option value="">{t("Etiqueta")}</option>
                  {labels.map((label) => (
                    <option key={label.id} value={label.id}>
                      {label.name}
                    </option>
                  ))}
                  {labelFilter && !labelsById[labelFilter] && (
                    <option value={labelFilter}>{t("Etiqueta eliminada")}</option>
                  )}
                </select>
                <ChevronDown
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Lista */}
      <div
        ref={listRef}
        role="list"
        aria-busy={loading}
        aria-label={t("Conversaciones")}
        className="flex-1 overflow-y-auto"
      >
        {loading ? (
          <>
            <ConvSkeleton />
            <ConvSkeleton />
            <ConvSkeleton />
            <ConvSkeleton />
          </>
        ) : error ? (
          <div className="flex flex-col items-center justify-center text-center py-12 px-6">
            <AlertTriangle className="w-8 h-8 mb-2 text-status-error" aria-hidden="true" />
            <p className="text-sm text-fg font-medium">
              {t("No se pudieron cargar las conversaciones")}
            </p>
            <p className="mt-1 text-xs text-fg-muted">
              {t("Revisa tu conexión o vuelve a intentarlo.")}
            </p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                className="mt-3 inline-flex items-center justify-center h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
              >
                {t("Reintentar")}
              </button>
            )}
          </div>
        ) : conversations.length === 0 ? (
          aiLogsError && !isSearching && !isFolderView ? (
            // Degradación de la fuente de IA. Solo habla cuando la lista vacía
            // ES la lista de IA: con una búsqueda activa el vacío es "esa
            // búsqueda no encontró nada" y dentro de una carpeta es "esta
            // carpeta está vacía" — culpar a la fuente en esos dos casos tapa
            // lo que el usuario acaba de hacer.
            <EmptyState
              icon={<Bot className="w-6 h-6" aria-hidden="true" />}
              title={t("Las atenciones por IA no están disponibles en este momento.")}
            />
          ) : isFolderView && !isSearching ? (
            // Carpeta vacía: distinta de "no hay conversaciones" — acá el
            // siguiente paso es MOVER chats, y hay que decirlo.
            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
              <Folder className="w-8 h-8 mb-2 text-fg-subtle" aria-hidden="true" />
              <p className="text-sm text-fg">
                {t("Sin conversaciones en esta carpeta")}
              </p>
              <p className="mt-1 text-[12px] text-fg-muted">
                {t("Mueve conversaciones desde el menú de un chat")}
              </p>
            </div>
          ) : (
            // A6 · Un estado vacío tiene que decir POR QUÉ está vacío y cuál es
            // el siguiente paso. Antes eran dos palabras sueltas ("Sin
            // resultados" / "No hay conversaciones") con 400px de blanco debajo.
            <div className="flex flex-col items-center justify-center text-center py-12 px-6 text-fg-muted">
              <Inbox className="w-8 h-8 mb-2 text-fg-subtle" aria-hidden="true" />
              <p className="text-sm text-fg font-medium">
                {isSearching ? t("Sin resultados") : t("No hay conversaciones")}
              </p>
              <p className="mt-1 text-xs text-fg-muted max-w-[30ch]">
                {isSearching
                  ? (messageSearchAlsoEmpty
                      ? t("Ningún caso ni mensaje coincide con «{{termino}}».")
                      : t("Ningún caso coincide con «{{termino}}».")
                    ).replace("{{termino}}", searchQuery.trim())
                  : isChannelView
                    ? t("Aún no llegan mensajes por este canal.")
                    : t("Cuando llegue un mensaje nuevo aparecerá acá.")}
              </p>
              {isSearching && messageSearchAlsoEmpty && (
                <p className="mt-1 text-xs text-fg-subtle max-w-[30ch]">
                  {t("Prueba con una palabra más corta o sin acentos.")}
                </p>
              )}
              {isSearching ? (
                <button
                  type="button"
                  onClick={() => onSearchChange("")}
                  className="mt-3 inline-flex items-center justify-center h-11 md:h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                >
                  {t("Limpiar búsqueda")}
                </button>
              ) : (
                isChannelView && (
                  <Link
                    // Los números se vinculan (o se revisan) en Integraciones →
                    // Canales de comunicación: HelpdeskSettings solo explica
                    // dónde queda, así que este atajo va derecho al sub-tab.
                    to="/admin/settings?tab=integrations&sub=channels"
                    className="mt-3 inline-flex items-center justify-center h-11 md:h-9 px-3 rounded-md border border-border bg-bg text-sm text-fg-muted hover:bg-bg-muted hover:text-fg transition-colors"
                  >
                    {t("Revisar canales")}
                  </Link>
                )
              )}
            </div>
          )
        ) : (
          grouped.map((group, gi) => (
            <div key={group.label ?? `group-${gi}`}>
              {group.label && (
                // Fondo SÓLIDO: con bg-bg-muted/30 las filas se transparentaban
                // por debajo del sticky al scrollear.
                // Color: fg-MUTED, no fg-subtle. fg-subtle sobre bg-muted da
                // 2.57:1 (light) y 3.21:1 (dark), bajo el mínimo AA de 4.5:1
                // para texto de 11px. fg-muted da 5.50:1 / 7.39:1 y mantiene
                // igual la jerarquía (es uppercase 11px, ya lee como micro-label).
                <div className="px-[16px] pt-[12px] pb-[4px] text-[11px] leading-[16px] font-semibold uppercase tracking-wider text-fg-muted bg-bg sticky top-0 z-10">
                  {group.pinned ? (
                    // La chincheta inclinada es el mismo glifo que el indicador
                    // de la fila: el encabezado se reconoce sin leer el rótulo.
                    <span className="inline-flex items-center gap-[6px]">
                      <Pin className="w-3 h-3 rotate-45" aria-hidden="true" />
                      {t(group.label)}
                    </span>
                  ) : (
                    t(group.label)
                  )}
                </div>
              )}
              {group.items.map((conv) => {
                const isNew = newIds.has(conv.id);
                const isActive = conv.id === selectedId;
                const isAI = conv.channel === "ai";
                const isGroup = !!conv.is_group;
                const userName = conversationDisplayName(conv, getDisplayName, t);
                // Pill de asignación: nombre plano para iniciales/compacto y
                // nombre con rol para el tooltip. assigned_by aún no llega desde
                // prod (backend en curso): el tooltip lo suma solo si existe.
                const agentFullName = conv.assigned_to
                  ? (getDisplayName && getDisplayName(conv.assigned_to)) ||
                    conv.assigned_to
                  : null;
                let assignedTitle = null;
                if (agentFullName) {
                  const agentTitleName =
                    (getDisplayName && getDisplayName(conv.assigned_to, true)) ||
                    conv.assigned_to;
                  assignedTitle = t("Asignada a {{agente}}").replace(
                    "{{agente}}",
                    agentTitleName
                  );
                  if (conv.assigned_by) {
                    const byName =
                      (getDisplayName && getDisplayName(conv.assigned_by)) ||
                      conv.assigned_by;
                    assignedTitle += ` · ${t("Asignada por {{nombre}}").replace(
                      "{{nombre}}",
                      byName
                    )}`;
                  }
                }
                const seedForAvatar = conv.user_id || conv.external_id || conv.id;
                const channelBadge = conv.channel && CHANNEL_BADGE[conv.channel];
                const channelName = CHANNEL_NAME[conv.channel]
                  ? t(CHANNEL_NAME[conv.channel])
                  : null;

                // Decidir el chip primario (máximo 1): SLA > razón transferencia > status.
                // No repetimos el status cuando ya está implícito en la sub-vista activa
                // (ej. "Sin asignar" → no chip "Pendiente"). Estados de excepción (SLA,
                // transferencia) siempre se muestran.
                const statusImpliedByView =
                  (inboxKey === "inbox.unassigned" && conv.status === "pending") ||
                  // En "Todas", 45 de 49 filas son pending: el chip amarillo deja
                  // de distinguir y solo suma tinta. Lo excepcional (SLA, pidió
                  // humano, fallo de IA, resuelta) sí se sigue mostrando, y la
                  // pill de agente asignado señala lo ya tomado.
                  (inboxKey === "inbox.all" && conv.status === "pending") ||
                  // En "IA en curso" todas están pendientes por definición
                  // (la IA solo atiende sin humano asignado).
                  (inboxKey === "inbox.ai_live" && conv.status === "pending") ||
                  // En el archivo TODAS están resueltas: repetir el chip en cada
                  // fila es ruido que no distingue nada.
                  (inboxKey === "inbox.resolved" && conv.status === "resolved");
                let primaryChip = null;
                if (conv.sla_breached) {
                  primaryChip = { label: t("SLA vencido") };
                } else if (conv.transfer_reason === "user_request") {
                  primaryChip = { label: t("Solicitó humano") };
                } else if (conv.transfer_reason === "ai_failure") {
                  primaryChip = { label: t("Fallo de IA") };
                } else if (!statusImpliedByView && conv.status === "pending") {
                  primaryChip = { label: t("Pendiente") };
                } else if (!statusImpliedByView && conv.status === "resolved") {
                  primaryChip = { label: t("Resuelta") };
                }

                // Reincidencia: legacy sin case_number = caso 1 (sin chip).
                const caseNumber = conv.case_number || 1;
                // El ticket del caso vigente reemplaza al chip de reincidencia:
                // "#T" es lo que el agente teclea para buscarlo. Sin ticket se
                // conserva el comportamiento de siempre ("Caso #N" solo desde 2).
                // El campo vacío ("" o 0) NO es un ticket: el despliegue del
                // backend va en paralelo y puede mandar el campo sin valor.
                const ticketNumber =
                  conv.ticket_number != null &&
                  conv.ticket_number !== "" &&
                  conv.ticket_number !== 0
                    ? conv.ticket_number
                    : null;
                const caseChip =
                  ticketNumber != null
                    ? {
                        label: `#${ticketNumber}`,
                        title: ticketLabel(ticketNumber, t),
                      }
                    : caseNumber > 1
                    ? {
                        label: `${t("Caso")} #${caseNumber}`,
                        title: t("El contacto volvió a escribir tras resolver el caso anterior"),
                      }
                    : null;
                // Chip de carpeta: suprimido cuando la vista activa ES esa
                // carpeta, y si la carpeta ya no existe en el índice no se
                // pinta nada (folder_id huérfano tras un borrado).
                const rowFolder =
                  foldersById &&
                  conv.folder_id &&
                  conv.folder_id !== activeFolderId
                    ? foldersById[conv.folder_id] || null
                    : null;
                const { visible: visibleLabels, hidden: hiddenLabels } = rowLabels(
                  conv,
                  labelsById,
                );

                // Comercio igual al nombre del contacto (el contacto ES el
                // comercio): repetirlo en la línea de datos no distingue nada.
                const commerceName =
                  conv.effective_commerce_id &&
                  conv.effective_commerce_name &&
                  conv.effective_commerce_name.trim().toLowerCase() !==
                    String(userName).trim().toLowerCase()
                    ? conv.effective_commerce_name
                    : null;
                // Estado excepcional de la fila: UNA señal, en texto teñido y
                // sin pill. SLA > pidió humano > fallo de IA > derivación IA >
                // IA a cargo > pendiente/resuelta (solo si la vista no lo
                // implica). Antes cada señal era su propia pill y una fila con
                // derivación + ticket + comercio ya leía como sopa de chips.
                let signal = null;
                if (primaryChip && conv.sla_breached) {
                  signal = { label: primaryChip.label, tone: "text-status-error", Icon: AlertTriangle };
                } else if (
                  primaryChip &&
                  (conv.transfer_reason === "user_request" || conv.transfer_reason === "ai_failure")
                ) {
                  signal = {
                    label: primaryChip.label,
                    tone: conv.transfer_reason === "ai_failure" ? "text-status-error" : "text-status-info",
                    Icon: conv.transfer_reason === "ai_failure" ? AlertTriangle : UserRound,
                  };
                } else if (conv.ai_escalated && conv.status !== "resolved") {
                  signal = {
                    label:
                      conv.ai_escalated_reason === "ai_derived"
                        ? t("Derivado por IA")
                        : t("Pide humano"),
                    tone: "text-status-warning",
                    Icon: conv.ai_escalated_reason === "ai_derived" ? Bot : UserRound,
                  };
                } else if (conv.byo_ai_active && inboxKey !== "inbox.ai_live") {
                  signal = { label: t("IA"), tone: "text-status-info", Icon: Bot };
                } else if (primaryChip) {
                  signal = { label: primaryChip.label, tone: "text-fg-muted", Icon: null };
                }
                // Datos planos de la línea 3, separados por "·" solo ENTRE
                // ítems reales. Cada uno en su propio elemento (el ticket se
                // busca tecleando "#N").
                const metaItems = [
                  caseChip && (
                    <span key="caso" className="shrink-0 tabular-nums" title={caseChip.title}>
                      {caseChip.label}
                    </span>
                  ),
                  commerceName && (
                    <span
                      key="comercio"
                      className="min-w-0 truncate"
                      title={t("Comercio: {{nombre}}").replace("{{nombre}}", commerceName)}
                    >
                      {commerceName}
                    </span>
                  ),
                  rowFolder && (
                    <span
                      key="carpeta"
                      className="inline-flex items-center gap-[4px] min-w-0 max-w-[120px] shrink-0"
                      title={t("Carpeta: {{nombre}}").replace("{{nombre}}", rowFolder.name)}
                    >
                      <span
                        className={cn(
                          "w-[6px] h-[6px] rounded-[2px] shrink-0",
                          (FOLDER_COLOR_STYLES[rowFolder.color] ||
                            FOLDER_COLOR_STYLES[DEFAULT_FOLDER_COLOR]).dot,
                        )}
                        aria-hidden="true"
                      />
                      <Folder className="w-3 h-3 shrink-0" aria-hidden="true" />
                      <span className="truncate">{rowFolder.name}</span>
                    </span>
                  ),
                ].filter(Boolean);
                const hasMetaLine = !!signal || metaItems.length > 0;
                const timeValue = conv.last_message_at || conv.created_at;
                const isPinned = !!pins?.has(conv.id);
                const canPinRow = canPin && isPinnable(conv);

                return (
                  // El disparador y el menú son HERMANOS de la fila, no hijos:
                  // un <button> dentro de un role="button" es HTML inválido y,
                  // además, sus clics burbujearían a onSelect (fijar abriría el
                  // chat). El wrapper relative ancla el menú a su fila.
                  <div key={conv.id} className="relative group/row">
                    <div
                      ref={(el) => setRowRef(conv.id, el)}
                      role="button"
                      tabIndex={0}
                      aria-current={isActive ? "true" : undefined}
                      aria-label={`${t("Conversación de")} ${userName}`}
                      // El nombre accesible lo fija aria-label (los hijos de un
                      // botón no cuentan): el estado "Fijado" va como descripción.
                      aria-describedby={isPinned ? `hd-pin-${conv.id}` : undefined}
                      onClick={() => {
                        // El click que sigue a "mantener presionado" NO abre la
                        // conversación: ese gesto ya se consumió en el menú.
                        const firedId = longPress.current.firedId;
                        if (firedId) {
                          longPress.current.firedId = null;
                          if (firedId === conv.id) return;
                        }
                        onSelect(conv);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(conv);
                        }
                      }}
                      // Clic derecho (y tecla Menú / Shift+F10, que emiten
                      // contextmenu sobre la fila enfocada) en todas las
                      // pantallas. Solo en filas fijables.
                      onContextMenu={
                        canPinRow
                          ? (e) => {
                              e.preventDefault();
                              // No se marca longPress.firedId: en escritorio no
                              // sigue un click y el siguiente click real (abrir
                              // la fila) se perdería.
                              clearLongPress();
                              openMenu(conv.id);
                            }
                          : undefined
                      }
                      // Mantener presionado (táctil). El click posterior se
                      // descarta arriba vía longPress.firedId.
                      onTouchStart={
                        canPinRow
                          ? (e) => {
                              const touch = e.touches?.[0];
                              longPress.current.firedId = null;
                              longPress.current.x = touch?.clientX ?? 0;
                              longPress.current.y = touch?.clientY ?? 0;
                              clearLongPress();
                              longPress.current.timer = setTimeout(() => {
                                longPress.current.firedId = conv.id;
                                openMenu(conv.id);
                              }, LONG_PRESS_MS);
                            }
                          : undefined
                      }
                      onTouchMove={
                        canPinRow
                          ? (e) => {
                              const touch = e.touches?.[0];
                              if (!touch) return;
                              const dx = touch.clientX - longPress.current.x;
                              const dy = touch.clientY - longPress.current.y;
                              // Scroll o arrastre real: ya no es un long press.
                              if (Math.hypot(dx, dy) > LONG_PRESS_SLOP_PX) clearLongPress();
                            }
                          : undefined
                      }
                      onTouchEnd={
                        canPinRow
                          ? (e) => {
                              clearLongPress();
                              // Al soltar tras un long press el navegador emite
                              // mousedown/click de compatibilidad sobre la fila:
                              // el mousedown cerraría el menú recién abierto
                              // ("clic fuera") y el click abriría el chat.
                              // preventDefault en touchend cancela ambos.
                              if (longPress.current.firedId === conv.id) e.preventDefault();
                            }
                          : undefined
                      }
                      onTouchCancel={canPinRow ? clearLongPress : undefined}
                      className={cn(
                        // scroll-mt: the sticky day header (~32px) must not cover a
                        // row scrolled into view (keyboard / selection).
                        "group relative flex gap-[12px] px-[16px] py-[12px] scroll-mt-[32px] border-b border-border/60 cursor-pointer transition-colors motion-reduce:transition-none",
                        "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
                        isActive
                          ? "bg-bg-subtle"
                          : conv.sla_breached
                          ? // Faint wash keeps overdue rows findable while scanning.
                            "bg-status-error-soft/25 hover:bg-status-error-soft/40"
                          : "hover:bg-bg-muted",
                        // En táctil (también tablets), mantener presionado no
                        // debe seleccionar texto ni abrir el menú nativo de iOS.
                        canPinRow &&
                          "[@media(hover:none)]:select-none [@media(hover:none)]:[-webkit-touch-callout:none]",
                      )}
                    >
                    {/* Avatar (36px). self-start: con varias líneas el wrapper
                        se estiraba y el badge de canal quedaba colgando abajo. */}
                    <div className="relative shrink-0 self-start">
                      {isAI ? (
                        <div className="w-[36px] h-[36px] rounded-full bg-bg-subtle text-fg-muted flex items-center justify-center">
                          <Bot className="w-[18px] h-[18px]" aria-hidden="true" />
                        </div>
                      ) : isGroup ? (
                        <div
                          className={cn(
                            "w-[36px] h-[36px] rounded-full flex items-center justify-center",
                            avatarTone(seedForAvatar),
                          )}
                          aria-hidden="true"
                        >
                          <Users className="w-[18px] h-[18px]" aria-hidden="true" />
                        </div>
                      ) : (
                        <ContactAvatar
                          src={conv.contact_profile_pic}
                          initial={getInitial(userName)}
                          toneClass={avatarTone(seedForAvatar)}
                          className="w-[36px] h-[36px] text-[13px]"
                        />
                      )}
                      {channelBadge && (
                        <span
                          role="img"
                          aria-label={channelName || undefined}
                          title={channelName || undefined}
                          className={cn(
                            "absolute -bottom-0.5 -right-0.5 w-[15px] h-[15px] rounded-full ring-2 ring-bg flex items-center justify-center",
                            channelBadge.bg,
                          )}
                        >
                          <channelBadge.icon
                            className="w-[9px] h-[9px] text-white"
                            aria-hidden="true"
                          />
                        </span>
                      )}
                    </div>

                    {/* Contenido: nombre/hora · preview/asignado · datos · etiquetas */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-[8px]">
                        <span
                          className={cn(
                            "min-w-0 text-[14px] leading-[20px] text-fg truncate",
                            // Actividad nueva desde que cargó la lista: nombre
                            // más pesado y preview a contraste completo.
                            isNew && !isActive ? "font-bold" : "font-semibold",
                          )}
                          title={userName}
                        >
                          {userName}
                        </span>
                        <span className="inline-flex items-center gap-[6px] shrink-0">
                          {/* Punto de actividad nueva: único uso del acento en
                              la fila (estado, no decoración). */}
                          {isNew && !isActive && (
                            <span
                              role="img"
                              className="w-[8px] h-[8px] rounded-full bg-brand-500"
                              aria-label={t("Nuevo")}
                              title={t("Nuevo")}
                            />
                          )}
                          {/* La hora se queda en fg-muted (no fg-subtle): sobre
                              bg fg-subtle da 2.68:1 en light, bajo AA. */}
                          <span
                            className="text-[12px] leading-[20px] text-fg-muted tabular-nums whitespace-nowrap"
                            title={timeValue ? dayjs(timeValue).format("DD-MM-YYYY HH:mm") : undefined}
                          >
                            {timeValue ? dayjs(timeValue).fromNow(true) : ""}
                          </span>
                        </span>
                      </div>

                      <div className="mt-[2px] flex items-center gap-[8px] min-w-0">
                        {conv.last_message_preview ? (
                          // En una línea de preview no hay negrita que valga: se
                          // quitan los delimitadores de WhatsApp (toPlainText).
                          // Solo en canales WhatsApp; en el resto el asterisco
                          // es literal.
                          (() => {
                            const preview = WHATSAPP_CHANNELS.includes(conv.channel)
                              ? toPlainText(conv.last_message_preview)
                              : conv.last_message_preview;
                            return (
                              <p
                                className={cn(
                                  "flex-1 min-w-0 text-[13px] leading-[18px] truncate",
                                  isNew && !isActive ? "text-fg font-medium" : "text-fg-muted",
                                )}
                                title={preview}
                              >
                                {preview}
                              </p>
                            );
                          })()
                        ) : (
                          <span className="flex-1" aria-hidden="true" />
                        )}
                        {/* Fijada: señal de estado, no botón. El menú de la
                            fila es quien la alterna. */}
                        {isPinned && (
                          <span
                            id={`hd-pin-${conv.id}`}
                            role="img"
                            aria-label={t("Fijado")}
                            title={t("Fijado")}
                            className="shrink-0 inline-flex text-fg-muted"
                          >
                            <Pin className="w-3.5 h-3.5 rotate-45" aria-hidden="true" />
                          </span>
                        )}
                        {/* Asignado: avatar de 20px con inicial (patrón
                            Chatwoot). El nombre completo va en el tooltip y en
                            texto para lectores de pantalla. */}
                        {agentFullName && (
                          <span
                            className="shrink-0 w-[20px] h-[20px] rounded-full bg-bg-subtle text-fg-muted text-[11px] leading-none font-semibold flex items-center justify-center ring-1 ring-border"
                            title={assignedTitle}
                          >
                            <span aria-hidden="true">{agentInitials(agentFullName)}</span>
                            <span className="sr-only">{shortAgentName(agentFullName)}</span>
                          </span>
                        )}
                      </div>

                      {hasMetaLine && (
                        <div className="mt-[6px] flex items-center gap-[6px] min-w-0 text-[12px] leading-[16px] text-fg-muted whitespace-nowrap overflow-hidden">
                          {signal && (
                            <span
                              className={cn(
                                "inline-flex items-center gap-[4px] shrink-0 font-medium",
                                signal.tone,
                              )}
                            >
                              {signal.Icon && (
                                <signal.Icon className="w-3 h-3 shrink-0" aria-hidden="true" />
                              )}
                              {signal.label}
                            </span>
                          )}
                          {metaItems.map((node, i) => (
                            <React.Fragment key={node.key}>
                              {(i > 0 || signal) && (
                                <span className="shrink-0 text-fg-subtle" aria-hidden="true">
                                  ·
                                </span>
                              )}
                              {node}
                            </React.Fragment>
                          ))}
                        </div>
                      )}

                      {/* Etiquetas: las únicas "chips" de la fila (máx. 2 + N). */}
                      {visibleLabels.length > 0 && (
                        <div className="mt-[6px] flex items-center gap-[4px] min-w-0 overflow-hidden">
                          {visibleLabels.map((label) => (
                            <LabelChip key={label.id} label={label} className="max-w-[120px]" />
                          ))}
                          {hiddenLabels.length > 0 && (
                            <span
                              className="inline-flex items-center shrink-0 px-[6px] h-[20px] rounded-full text-fg-muted text-[11px] font-medium tabular-nums"
                              title={hiddenLabels.map((l) => l.name).join(", ")}
                            >
                              <span aria-hidden="true">+{hiddenLabels.length}</span>
                              <span className="sr-only">
                                {t("{{count}} etiquetas más: {{nombres}}")
                                  .replace("{{count}}", hiddenLabels.length)
                                  .replace("{{nombres}}", hiddenLabels.map((l) => l.name).join(", "))}
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    </div>

                    {/* Disparador del menú (escritorio). Tapa la hora mientras
                        se pasa el mouse, como WhatsApp Web; `hover:none` lo
                        oculta en pantallas táctiles — invisible pero tocable
                        robaría el tap que abre la conversación. */}
                    {!isMobile && canPinRow && (
                      <button
                        type="button"
                        ref={(el) => setTriggerRef(conv.id, el)}
                        onClick={() =>
                          menu?.id === conv.id ? closeMenu(false) : openMenu(conv.id, "trigger")
                        }
                        aria-haspopup="menu"
                        aria-expanded={menu?.id === conv.id}
                        aria-label={t("Opciones del chat")}
                        title={t("Opciones del chat")}
                        className={cn(
                          "absolute right-[10px] top-[10px] inline-flex w-[24px] h-[24px] items-center justify-center rounded-md border border-border bg-bg text-fg-muted shadow-soft transition-opacity duration-150 motion-reduce:transition-none",
                          "hover:text-fg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                          "[@media(hover:none)]:hidden",
                          menu?.id === conv.id
                            ? "opacity-100"
                            : "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
                        )}
                      >
                        <ChevronDown className="w-4 h-4" aria-hidden="true" />
                      </button>
                    )}

                    {menu?.id === conv.id && (
                      <ConversationRowMenu
                        pinned={isPinned}
                        placement={menu.placement}
                        onTogglePin={() => onTogglePin(conv)}
                        onClose={closeMenu}
                        ignoreEl={triggerRefs.current.get(conv.id) || null}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>
    </>
  );
}
